// 미수금 원장 (2026-10-23 거래명세서 1단계 — 명세 §4, 계약 §5)
// 원칙: 잔액은 저장하지 않고 볼 때마다 계산한다.
//   받을 돈 = 살아 있는 발행본 합계(totalAmount, 공급가액) + 연결된 '발행 완료' 과세 계산서 세액 − 입금(취소 제외)
//   시작 잔액 없음(대표 결정 10-23 — 0원부터).
// 입금 기록(receivablePayments) 읽기·쓰기는 기공소 대표 본인만(rules). 환자 정보 없음. console 에는 id·건수만.
import {
    collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { partnerKeyOfStatement, isMemberPartnerKey, kstYmd, toDateAny } from './billingCycle';

export const PAYMENT_METHODS = ['transfer', 'cash', 'card', 'other'];
export const PAYMENT_METHOD_LABEL = { transfer: '계좌이체', cash: '현금', card: '카드', other: '기타' };
const PAID_AT_RE = /^20\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

// 외주 명세서 종류 (CAD 디자인 외주·제조 의뢰) — 원장은 치과 거래만 (statement/paymentState·closingDraft 와 같은 기준)
export const OUTSOURCE_KINDS = ['design_outsource', 'outsource_mf'];
const isOutsource = (s) => OUTSOURCE_KINDS.includes(s?.kind);

// 살아 있는 발행본 — 치과 거래만(외주 제외), 삭제 표시·정정 원본 제외 (명세 §4-4)
export const isLiveIssuedStatement = (s) =>
    !!s && ['confirmed', 'published'].includes(s.status) &&
    s.deletedBySender !== true && !isOutsource(s);

// 계산서 '발행 완료' 상태 (TaxInvoiceDetail STATUS_MAP) / 취소류 (excelExport isVoidInvoice·EXCLUDED_STATUS)
const ISSUED_INVOICE = new Set(['issued', 'sent_to_nts', 'nts_confirmed']);
const VOID_INVOICE = new Set(['cancelled', 'canceled', 'error', 'rejected']);
// 과세 판정: isTaxExempt === true 만 면세. 필드가 없는 옛 계산서는 과세로 본다
//   (상세 화면 TaxInvoiceDetail.js:336 `invoice.isTaxExempt || false` 와 같은 기준 — 세액 0원이면 어차피 0)
export const isCountableTaxInvoice = (inv) =>
    !!inv && ISSUED_INVOICE.has(inv.status) && !VOID_INVOICE.has(inv.status) &&
    inv.electronicStatus !== 'cancelled' &&   // 팝빌 취소 — 상태 조회는 electronicStatus 만 바꾸고 status 는 'issued' 그대로 (계약 §3, 2026-10-24)
    inv.isTaxExempt !== true && Number(inv.taxAmount) !== 0 && !isNaN(Number(inv.taxAmount));

// 귀속일 = 기간 종료일 → 발행일 → 작성일 (statementView.statementMonthKeyDate 와 같은 순서) → KST 'YYYY-MM-DD'
export const statementAttrDate = (s) => kstYmd(s?.endDate) || kstYmd(s?.publishedAt) || kstYmd(s?.createdAt);

const intAmount = (v) => Math.round(Number(v) || 0);

// ── 원천 데이터 읽기 (기공소 단위, where 필터 필수) + 세션 캐시 ─────────────────
// 같은 세션에서 목록·거래처·작성 화면이 원장을 여러 번 쓰므로 원천 배열을 기공소별로 기억한다.
//   입금 기록 추가·취소는 자동으로 캐시를 지운다. 명세서 발행·삭제 뒤에는 호출부가 invalidateReceivables(labId).
//   안전판: 10분이 지나면 다시 읽는다.
const CACHE_TTL_MS = 10 * 60 * 1000;
const sourceCache = new Map();   // labId → { at, promise }

/** 원장 캐시 지우기 (labId 없으면 전부) */
export function invalidateReceivables(labId) {
    if (labId) sourceCache.delete(labId); else sourceCache.clear();
}

/**
 * 원천(명세서·계산서·입금) 1회 읽기 + 세션 캐시 — 공용 로더 (2026-10-24 재점검 P1-4).
 *   목록 카드 ①(missingClaims.computeUnbilledSummary)과 ③(loadReceivables)이 각각 명세서 전량을 읽던 것을 하나로 합친다.
 *   범위 제한(status·기간)은 두지 않는다: 받을 돈은 날짜 제한 없는 전량이 필요하고, 초안·삭제 표시본도 둘 다 쓴다
 *   (초안 금액·'초안에만'·정정 원본 추적·'지워진 명세서에 있었음'). 1단계 정의 "청구됨 = 살아 있는 발행본 전부"도 그대로.
 * @returns {Promise<{statements, invoices, payments}>}
 */
export async function loadLedgerSources(labId, { force = false } = {}) {
    const hit = sourceCache.get(labId);
    if (!force && hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;
    const promise = fetchLedgerSources(labId);
    sourceCache.set(labId, { at: Date.now(), promise });
    try {
        return await promise;
    } catch (e) {
        sourceCache.delete(labId);   // 실패한 읽기는 기억하지 않는다
        throw e;
    }
}

async function fetchLedgerSources(labId) {
    const [stSnap, invSnap, paySnap] = await Promise.all([
        getDocs(query(collection(db, 'transactionStatements'), where('fromUserId', '==', labId))),
        getDocs(query(collection(db, 'taxInvoices'), where('createdBy', '==', labId))),
        getDocs(query(collection(db, 'receivablePayments'), where('labId', '==', labId))),
    ]);
    return {
        statements: stSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        invoices: invSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        payments: paySnap.docs.map(d => ({ id: d.id, ...d.data() })),
    };
}

/**
 * 순수 계산 — 원천 배열로 거래처별 원장을 만든다 (단위 시험용으로 분리).
 * @param {{statements, invoices, payments}} src
 * @param {{asOf?: 'YYYY-MM-DD', excludeStatementId?: string|string[], attributionDate?: 'YYYY-MM-DD'}} opt
 *   asOf            : 귀속일·입금일 ≤ asOf 만
 *   excludeStatementId : 뺄 명세서 id (배열 가능 — 정정본 발행 때 [이번 id, correctsId])
 *   attributionDate : 귀속일이 이 날짜보다 뒤인 명세서 제외 (이번 명세서보다 뒤 달 명세서 빼기, 명세 §4-5)
 */
export function buildLedger({ statements = [], invoices = [], payments = [] }, opt = {}) {
    const { asOf = null, excludeStatementId = null, attributionDate = null } = opt;
    const excludeIds = new Set((Array.isArray(excludeStatementId) ? excludeStatementId : [excludeStatementId]).filter(Boolean));
    const byPartner = {};
    const entryOf = (key, name) => {
        if (!byPartner[key]) {
            byPartner[key] = {
                partnerKey: key, partnerName: name || '', billed: 0, tax: 0, paid: 0, balance: 0,
                isMember: isMemberPartnerKey(key), taxNeedsReview: false, lastPaidAt: null,
                draftAmount: 0, draftCount: 0, disputedCount: 0, statements: [],
            };
        } else if (name && !byPartner[key].partnerName) {
            byPartner[key].partnerName = name;
        }
        return byPartner[key];
    };

    // 계산서 → 연결 명세서 id 별 세액. 정정 원본(deletedBySender + correctedTo)에 붙은 계산서는 정정본으로 따라간다
    const stmtById = new Map(statements.map(s => [s.id, s]));
    const resolveStatementId = (sid) => {
        let cur = sid;
        for (let i = 0; i < 10 && cur; i++) {
            const st = stmtById.get(cur);
            if (!st || st.deletedBySender !== true || !st.correctedTo) return cur;
            cur = st.correctedTo;
        }
        return cur;
    };
    const taxByStatement = new Map();
    for (const inv of invoices) {
        if (!isCountableTaxInvoice(inv)) continue;
        if (!inv.linkedStatementId) continue;   // 연결 없는 계산서는 거래처를 알 수 없어 원장에 넣지 않는다 (unlinkedTaxCount 로 셈)
        const sid = resolveStatementId(inv.linkedStatementId);
        taxByStatement.set(sid, (taxByStatement.get(sid) || 0) + intAmount(inv.taxAmount));
    }
    const linkedIds = new Set();
    let manualCount = 0;

    for (const s of statements) {
        if (isOutsource(s)) continue;
        const key = partnerKeyOfStatement(s);
        if (!key) { if (s.status !== 'draft') manualCount += 1; continue; }
        if (s.status === 'draft') {
            const e = entryOf(key, s.toUserName);
            e.draftAmount += intAmount(s.totalAmount);
            e.draftCount += 1;
            continue;
        }
        if (!isLiveIssuedStatement(s)) continue;
        if (excludeIds.has(s.id)) continue;
        const date = statementAttrDate(s);
        if (asOf && date && date > asOf) continue;
        if (attributionDate && date && date > attributionDate) continue;
        const e = entryOf(key, s.toUserName);
        const amount = intAmount(s.totalAmount);
        const tax = taxByStatement.get(s.id) || 0;
        if (taxByStatement.has(s.id)) linkedIds.add(s.id);
        e.billed += amount;
        e.tax += tax;
        if (tax !== 0) e.taxNeedsReview = true;   // 기공물(면세 기본) 명세서에 과세 계산서 → 세무 확인 배지
        if (s.settlementStatus === 'disputed') e.disputedCount += 1;
        e.statements.push({
            id: s.id, statementNumber: s.statementNumber || '', amount, tax, due: amount + tax, date,   // 번호는 원장 줄 표시용 (계약 §3, 2026-10-24)
            createdMs: toDateAny(s.createdAt)?.getTime() || 0,
            settlementStatus: s.settlementStatus || 'pending',
        });
    }
    // 원장에 못 붙은 과세 계산서 (연결 없음·삭제된 명세서·제외 조건) — 숫자로만 남김
    let unlinkedTaxCount = 0;
    for (const inv of invoices) {
        if (isCountableTaxInvoice(inv) && (!inv.linkedStatementId || !linkedIds.has(resolveStatementId(inv.linkedStatementId)))) unlinkedTaxCount += 1;
    }

    for (const p of payments) {
        if (p.voidedAt) continue;
        if (!p.partnerKey) continue;
        if (asOf && p.paidAt && p.paidAt > asOf) continue;
        const e = entryOf(p.partnerKey, p.partnerName);
        // 'adjust'(후속)도 양수 = 미수 줄임 — 명세 §4-3
        e.paid += intAmount(p.amount);
        if (p.paidAt && (!e.lastPaidAt || p.paidAt > e.lastPaidAt)) e.lastPaidAt = p.paidAt;
    }

    let total = 0;
    Object.values(byPartner).forEach(e => {
        e.statements.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.createdMs - b.createdMs));
        e.balance = e.billed + e.tax - e.paid;
        if (e.balance > 0) total += e.balance;   // 더 받은 거래처(음수)는 다른 거래처 미수를 상쇄하지 않는다
    });
    return { byPartner, total, manualCount, unlinkedTaxCount };
}

/**
 * 거래처별 받을 돈 (오늘 기준, 날짜 제한 없음).
 * @returns {Promise<{ byPartner, total, error, manualCount, unlinkedTaxCount }>}
 *   byPartner[key] = { partnerKey, partnerName, billed, tax, paid, balance, isMember, taxNeedsReview, lastPaidAt,
 *                      draftAmount, draftCount, disputedCount, statements:[{id, amount, tax, due, date}] (오래된 순) }
 *   실패 시 throw 하지 않고 error 에 메시지 (화면에서 알림)
 */
export async function loadReceivables(labId, { force = false } = {}) {
    if (!labId) return { byPartner: {}, total: 0, error: 'labId 없음', manualCount: 0, unlinkedTaxCount: 0 };
    try {
        const src = await loadLedgerSources(labId, { force });
        const r = buildLedger(src);
        return { ...r, error: null };
    } catch (e) {
        console.error('[receivables] 원장 계산 실패', e?.code || e?.message || e);
        return { byPartner: {}, total: 0, error: e?.code || e?.message || '원장 계산 실패', manualCount: 0, unlinkedTaxCount: 0 };
    }
}

/**
 * 전월 미수 (발행 시점에 한 번 계산해 명세서 carryOver 에 고정 — 명세 §4-5).
 *  = asOf 이전(포함) 귀속 발행본 + 세액 − asOf 이전(포함) 입금, 이번 명세서 제외. 음수도 그대로 돌려준다(≤0 이면 표시 안 함은 호출부).
 * @param excludeStatementId  string | string[] (정정본 발행 때 [이번 id, correctsId])
 * @param opt  { force?: boolean (발행 직전 = true 로 새로 읽기), attributionDate?: Date|'YYYY-MM-DD' (귀속일이 이보다 뒤인 명세서 제외) }
 * @returns {Promise<{ amount: number, asOf: 'YYYY-MM-DD' }>}  읽기 실패는 throw (발행 확인창에서 알릴 것)
 */
export async function computeCarryOver(labId, partnerKey, asOf = new Date(), excludeStatementId = null, opt = {}) {
    const { force = false, attributionDate = null } = opt || {};
    const asOfYmd = kstYmd(asOf) || kstYmd(new Date());
    if (!labId || !partnerKey) return { amount: 0, asOf: asOfYmd };
    const src = await loadLedgerSources(labId, { force });
    const { byPartner } = buildLedger(src, {
        asOf: asOfYmd, excludeStatementId,
        attributionDate: attributionDate ? kstYmd(attributionDate) : null,
    });
    const e = byPartner[partnerKey];
    return { amount: e ? e.balance : 0, asOf: asOfYmd };
}

/** 입금 기록 추가 → 새 문서 id. 형식 오류는 throw (rules 와 같은 검사) */
export async function addPayment({ labId, partnerKey, partnerName, amount, paidAt, method, memo }) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('로그인이 필요합니다');
    if (!labId || labId !== uid) throw new Error('입금 기록은 기공소 대표 계정에서만 할 수 있습니다');
    if (!partnerKey || typeof partnerKey !== 'string') throw new Error('거래처가 없습니다');
    const amt = Number(amount);
    if (!Number.isInteger(amt) || amt <= 0) throw new Error('금액은 1원 이상 정수로 입력해 주세요');
    if (!PAID_AT_RE.test(String(paidAt || ''))) throw new Error('입금 날짜 형식이 올바르지 않습니다');
    if (!PAYMENT_METHODS.includes(method)) throw new Error('입금 방법을 골라 주세요');
    const m = String(memo || '').trim();
    if (m.length > 40) throw new Error('메모는 40자까지 적을 수 있습니다');
    const data = {
        labId, partnerKey,
        partnerName: String(partnerName || '').slice(0, 60),
        kind: 'payment', amount: amt, paidAt, method,
        ...(m ? { memo: m } : {}),
        source: 'manual',
        createdAt: serverTimestamp(),
        createdBy: uid,
    };
    const ref = await addDoc(collection(db, 'receivablePayments'), data);
    invalidateReceivables(labId);
    console.log('[receivables] 입금 기록 추가', ref.id);
    return ref.id;
}

/** 입금 기록 취소(삭제 아님 — 취소선·누가 언제 남김) */
export async function voidPayment(paymentId, reason) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('로그인이 필요합니다');
    if (!paymentId) throw new Error('입금 기록 id 없음');
    const r = String(reason || '').trim().slice(0, 40);
    await updateDoc(doc(db, 'receivablePayments', paymentId), {
        voidedAt: serverTimestamp(),
        voidedBy: uid,
        ...(r ? { voidReason: r } : {}),
    });
    invalidateReceivables();   // 취소는 labId 를 모르므로 전부 지운다
    console.log('[receivables] 입금 기록 취소', paymentId);
}

/** 거래처 입금 기록 목록(취소 포함) — 입금일 최신순 */
export async function listPayments(labId, partnerKey) {
    if (!labId || !partnerKey) return [];
    const snap = await getDocs(query(collection(db, 'receivablePayments'),
        where('labId', '==', labId), where('partnerKey', '==', partnerKey)));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.paidAt < b.paidAt ? 1 : a.paidAt > b.paidAt ? -1 :
            (toDateAny(b.createdAt)?.getTime() || 0) - (toDateAny(a.createdAt)?.getTime() || 0)));
}

/**
 * 명세서 한 장의 입금 상태 — 거래처 입금을 오래된 명세서부터 갚은 것으로 본다(FIFO, 명세 §4-4).
 * @returns {{ state: 'paid'|'partial'|'unpaid', remaining: number, due: number }}
 *   원장에 없는 명세서(초안·삭제·직접 입력)는 unpaid, remaining = 0 이 아닌 그 명세서 금액
 */
export function statementPaymentDetail(stmt, byPartnerEntry) {
    const fallbackDue = intAmount(stmt?.totalAmount);
    if (!stmt || !byPartnerEntry || !Array.isArray(byPartnerEntry.statements)) {
        return { state: 'unpaid', remaining: fallbackDue, due: fallbackDue };
    }
    // 마이너스 명세서(감액분)는 입금 여력에 더한다
    let left = (Number(byPartnerEntry.paid) || 0) +
        byPartnerEntry.statements.reduce((a, s) => a + Math.max(0, -(Number(s.due) || 0)), 0);
    for (const s of byPartnerEntry.statements) {
        const due = Number(s.due) || 0;
        if (due <= 0) {
            if (s.id === stmt.id) return { state: 'paid', remaining: 0, due };
            continue;
        }
        const covered = Math.max(0, Math.min(due, left));
        left -= covered;
        if (s.id === stmt.id) {
            if (covered >= due) return { state: 'paid', remaining: 0, due };
            return { state: covered > 0 ? 'partial' : 'unpaid', remaining: due - covered, due };
        }
    }
    return { state: 'unpaid', remaining: fallbackDue, due: fallbackDue };
}

/** 계약 §5: 'paid' | 'partial' | 'unpaid' */
export const statementPaymentState = (stmt, byPartnerEntry) => statementPaymentDetail(stmt, byPartnerEntry).state;
